import React, { useEffect, useRef, useState } from 'react';
import type { AdminApiClient } from './api-client';
import type {
  MiniappPageLinkResponse,
  MiniappPageLinkTargetType,
  MiniappLinkEnvironmentVersion,
} from '@travel/contracts';
import './miniapp-page-links.css';

type TargetType = MiniappPageLinkTargetType;
type EnvironmentVersion = MiniappLinkEnvironmentVersion;
interface Column {
  name: string;
  category?: string;
  topicLabel?: string;
}
type Entry = MiniappPageLinkResponse;

function CopyField({
  label,
  value,
  disabled = false,
}: {
  label: string;
  value: string | null;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState('');
  useEffect(() => setFeedback(''), [value]);
  async function copy() {
    if (!value || disabled) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(value);
      setFeedback('已复制');
    } catch {
      input.current?.focus();
      input.current?.select();
      setFeedback('文本已选中，请按 Ctrl+C 复制');
    }
  }
  return (
    <div className="mp-links-field">
      <label>
        {label}
        <div className="mp-links-copy-row">
          <input
            ref={input}
            readOnly
            value={value ?? ''}
            aria-label={label}
            placeholder="尚未生成"
          />
          <button type="button" disabled={disabled || !value} onClick={() => void copy()}>
            复制
          </button>
        </div>
      </label>
      {feedback && <small role="status">{feedback}</small>}
    </div>
  );
}

export function MiniappPageLinks({
  api,
  appId,
  targetType,
  targetId,
  title = '小程序推广地址',
}: {
  api: AdminApiClient;
  appId: string;
  targetType: TargetType;
  targetId?: string;
  title?: string;
}) {
  const [environmentVersion, setEnvironmentVersion] = useState<EnvironmentVersion>('release');
  const [column, setColumn] = useState('');
  const [entry, setEntry] = useState<Entry>();
  const [columns, setColumns] = useState<Column[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const generation = useRef(0);
  const command = useRef<AbortController>();
  useEffect(() => {
    setColumn('');
    setColumns([]);
  }, [appId, targetType, targetId]);
  const selectedColumn = columns.find((item) => JSON.stringify(item) === column);
  const effectiveTargetType = selectedColumn ? 'COLUMN' : targetType;
  const base = `/api/admin/m6/apps/${encodeURIComponent(appId)}/page-links`;
  const query = new URLSearchParams({ targetType: effectiveTargetType, environmentVersion });
  if (targetId) query.set('targetId', targetId);
  if (selectedColumn?.category) query.set('category', selectedColumn.category);
  if (selectedColumn?.topicLabel) query.set('topicLabel', selectedColumn.topicLabel);
  const queryString = query.toString();
  useEffect(() => {
    const active = new AbortController();
    const id = ++generation.current;
    command.current?.abort();
    setGenerating(false);
    setEntry(undefined);
    setError('');
    if (!appId || (targetType !== 'HOME' && !targetId)) {
      setLoading(false);
      return () => active.abort();
    }
    setLoading(true);
    const timeout = setTimeout(() => {
      if (generation.current !== id) return;
      active.abort();
      setLoading(false);
      setError('读取地址超时，请点击重新读取');
    }, 12_000);
    void api
      .request<Entry>(`${base}?${queryString}`, { signal: active.signal, cache: 'no-store' })
      .then((value) => {
        if (active.signal.aborted || generation.current !== id) return;
        setEntry(value);
        if (Array.isArray(value.columns) && effectiveTargetType !== 'COLUMN')
          setColumns(value.columns);
      })
      .catch((cause: unknown) => {
        if (!active.signal.aborted && generation.current === id)
          setError(cause instanceof Error ? cause.message : '地址读取失败，请重试');
      })
      .finally(() => {
        clearTimeout(timeout);
        if (!active.signal.aborted && generation.current === id) setLoading(false);
      });
    return () => {
      clearTimeout(timeout);
      active.abort();
    };
  }, [api, appId, targetType, targetId, base, queryString, effectiveTargetType, refresh]);
  useEffect(() => () => command.current?.abort(), []);
  async function generate() {
    if (generating || !entry?.available) return;
    const active = new AbortController();
    command.current = active;
    const id = generation.current;
    setGenerating(true);
    setError('');
    const timeout = setTimeout(() => {
      if (generation.current !== id || command.current !== active) return;
      active.abort();
      command.current = undefined;
      setGenerating(false);
      setError('微信地址生成超时，可重新获取；已保存的有效链接不受影响');
    }, 20_000);
    try {
      const value = await api.request<Entry>(base, {
        method: 'POST',
        signal: active.signal,
        body: JSON.stringify({
          targetType: effectiveTargetType,
          ...(targetId ? { targetId } : {}),
          ...(selectedColumn?.category ? { category: selectedColumn.category } : {}),
          ...(selectedColumn?.topicLabel ? { topicLabel: selectedColumn.topicLabel } : {}),
          environmentVersion,
        }),
      });
      if (!active.signal.aborted && generation.current === id) setEntry(value);
    } catch (cause) {
      if (!active.signal.aborted && generation.current === id)
        setError(cause instanceof Error ? cause.message : '微信地址生成失败，请重试');
    } finally {
      clearTimeout(timeout);
      if (command.current === active) command.current = undefined;
      if (!active.signal.aborted && generation.current === id) setGenerating(false);
    }
  }
  const url = entry?.urlLink;
  useEffect(() => {
    if (!url) return;
    const remaining = new Date(url.expiresAt).getTime() - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return;
    const timeout = setTimeout(() => setClock(Date.now()), Math.min(remaining + 1, 2_147_483_647));
    return () => clearTimeout(timeout);
  }, [url, clock]);
  const validUrl = Boolean(
    url &&
    new Date(url.expiresAt).getTime() > Math.max(clock, Date.now()) &&
    url.environmentVersion === environmentVersion &&
    entry?.available,
  );
  return (
    <section className="mp-links-panel" aria-label={title}>
      <header>
        <div>
          <h3>{title}</h3>
          <p>复制到广告平台的微信小程序落地页设置，直接打开指定内容。</p>
        </div>
        <span
          className={
            environmentVersion === 'release'
              ? 'mp-links-badge'
              : 'mp-links-badge mp-links-badge--trial'
          }
        >
          {environmentVersion === 'release' ? '正式版投放' : '体验版测试'}
        </span>
      </header>
      <div className="mp-links-options">
        <label>
          打开版本
          <select
            value={environmentVersion}
            onChange={(event) => setEnvironmentVersion(event.target.value as EnvironmentVersion)}
          >
            <option value="release">正式版（广告投放）</option>
            <option value="trial">体验版（测试使用）</option>
          </select>
        </label>
        {columns.length > 0 && (
          <label>
            落地内容
            <select
              aria-label="选择推广栏目"
              value={column}
              onChange={(event) => setColumn(event.target.value)}
            >
              <option value="">整个城市页</option>
              {columns.map((item) => (
                <option key={JSON.stringify(item)} value={JSON.stringify(item)}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          disabled={loading || generating || !appId}
          onClick={() => setRefresh((value) => value + 1)}
        >
          重新读取
        </button>
      </div>
      {!appId && <p className="mp-links-notice">请先在后台顶部选择要推广的小程序。</p>}
      {loading && <p role="status">正在读取页面地址…</p>}
      {error && (
        <p className="mp-links-error" role="alert">
          {error}
        </p>
      )}
      {entry && (
        <>
          <div className="mp-links-target">
            <strong>
              {entry.appName} · {entry.targetName}
            </strong>
          </div>
          {!entry.available && (
            <p className="mp-links-notice">
              {entry.reason || '当前内容尚不能作为小程序落地页，请先完成发布配置。'}
            </p>
          )}
          <CopyField label="小程序 AppID" value={entry.wechatAppId} />
          <CopyField
            label="页面路径（含必要参数）"
            value={entry.pathWithQuery}
            disabled={!entry.available}
          />
          <details className="mp-links-details">
            <summary>平台单独填写路径和参数时</summary>
            <CopyField label="纯页面路径" value={entry.pagePath} disabled={!entry.available} />
            <CopyField label="页面参数（Query）" value={entry.query} disabled={!entry.available} />
          </details>
          <div className="mp-links-url-heading">
            <strong>唤起 URL（微信 URL Link）</strong>
            <button
              type="button"
              disabled={loading || generating || !entry.available}
              onClick={() => void generate()}
            >
              {generating ? '微信生成中…' : validUrl ? '获取有效 URL' : '生成唤起 URL'}
            </button>
          </div>
          {validUrl && url ? (
            <>
              <CopyField label="URL 地址" value={url.url} />
              <p className="mp-links-expiry">
                有效至{' '}
                {new Date(url.expiresAt).toLocaleString('zh-CN', {
                  timeZone: 'Asia/Shanghai',
                  hour12: false,
                })}
                。到期前获取新地址并更新广告设置。
              </p>
            </>
          ) : (
            <p className="mp-links-hint">点击生成后显示微信返回的有效地址，页面路径可独立复制。</p>
          )}
          <p className="mp-links-hint">
            {environmentVersion === 'release'
              ? '正式版链接需对应页面已在微信发布；广告平台选择微信小程序时填写 AppID 和页面路径，要求 URL 时使用上方 URL Link。'
              : '体验版仅供已授权体验成员测试，不能用于公开广告投放。'}
          </p>
        </>
      )}
    </section>
  );
}
